// (c) chaehongee. Confidential and proprietary.

import { useState, type ReactElement, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './Sessions.module.css'
import { useAppDispatch } from '../../store/hooks/hooks'
import { upsertSession, setSessionError, setSessionLoading } from '../../store/slices/sessionSlice'
import { setPendingInitialSelection } from '../../store/slices/selectionSlice'
import { createSession, getSessionInfo, listSessions } from '../../services/session'
import type { SessionListItem } from 'models-ts/session'
import { ImageIcon, PlusIcon, Globe, Lock, Layers, Video } from 'lucide-react'
import { formatRelativeTime } from '../../services/time'
import { useAuth } from '../../store/hooks/useAuth'
import { LandingHeader } from './LandingHeader'
import { isDev } from '../../services/env'
import { openTab, setActiveTab, markSessionFresh, clearSessionFresh } from '../../store/slices/sessionTabsSlice'
import { WhatsNew } from '../WhatsNew/WhatsNew'
import { useWhatsNew } from '../WhatsNew/useWhatsNew'

export function Sessions(): ReactElement {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const whatsNew = useWhatsNew(!!user)
  const [items, setItems] = useState<SessionListItem[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingBarState, setLoadingBarState] = useState<'hidden' | 'loading' | 'completing'>('hidden')
  const [scope, setScope] = useState<'public' | 'my' | 'all'>(isDev() ? 'all' : 'public')
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const isLoadingRef = useRef(false)
  const PAGE_SIZE = 24

  // Switch scope handler
  const handleScopeChange = (newScope: 'public' | 'my' | 'all') => {
    if (newScope === scope) return
    if (newScope === 'my' && !user) {
      const returnUrl = window.location.pathname
      window.dispatchEvent(new CustomEvent('show:login', { detail: { returnUrl } }))
      return
    }

    setItems([])
    setPage(1)
    setHasMore(true)
    setInitialLoadDone(false)
    fetchedPagesRef.current = new Set()
    setScope(newScope)
  }

  const handleCreate = useCallback(async () => {
    if (!user) {
      const returnUrl = window.location.pathname
      window.dispatchEvent(new CustomEvent('show:login', { detail: { returnUrl } }))
      return
    }

    const loadingStartTime = Date.now()
    const MIN_LOADING_TIME = 800

    try {
      const session = await createSession()
      localStorage.setItem('session_id', session.id)
      dispatch(openTab(session.id))
      dispatch(setActiveTab(session.id))
      dispatch(markSessionFresh(session.id))
      dispatch(setSessionLoading({ sessionId: session.id, isLoading: true }))
      dispatch(setSessionError({ sessionId: session.id, error: null }))
      dispatch(upsertSession({ ...session, nodes: [], commits: [], messages: [] } as any))
      try {
        window.history.pushState({}, '', `/session/${session.id}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
      } catch { }
      ;(async () => {
        try {
          const info = await getSessionInfo(session.id)
          dispatch(upsertSession(info))
          try {
            const commits = Array.isArray((info as any).commits) ? (info as any).commits : []
            const nodes = Array.isArray((info as any).nodes) ? (info as any).nodes : []
            let lastNodeId: string | null = null
            if (commits.length > 0) {
              const lastCommit = [...commits].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).at(-1)
              lastNodeId = lastCommit?.node_to_id ?? null
            }
            if (!lastNodeId && nodes.length > 0) {
              const lastNode = [...nodes].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).at(-1)
              lastNodeId = lastNode?.id ?? null
            }
            if (lastNodeId) dispatch(setPendingInitialSelection({ sessionId: session.id, nodeId: lastNodeId }))
          } catch { }

          const elapsedTime = Date.now() - loadingStartTime
          const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime)
          setTimeout(() => {
            dispatch(setSessionLoading({ sessionId: session.id, isLoading: false }))
          }, remainingTime)
        } catch (e: any) {
          dispatch(setSessionError({ sessionId: session.id, error: e?.message ?? 'Failed to load session details' }))
          const elapsedTime = Date.now() - loadingStartTime
          const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime)
          setTimeout(() => {
            dispatch(setSessionLoading({ sessionId: session.id, isLoading: false }))
          }, remainingTime)
        }
      })()
    } catch (e: any) {
      dispatch(setSessionError({ sessionId: 'create', error: e?.message ?? 'Failed to create session' }))
    }
  }, [dispatch, user])

  // Sync loading bar state with isLoading
  useEffect(() => {
    if (isLoading) {
      setLoadingBarState('loading')
    } else if (loadingBarState === 'loading') {
      setLoadingBarState('completing')
      const timer = setTimeout(() => {
        setLoadingBarState('hidden')
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [isLoading, loadingBarState])

  // Track which pages have been fetched to avoid duplicate requests
  const fetchedPagesRef = useRef<Set<string>>(new Set())

  // Fetch sessions for current page and scope
  useEffect(() => {
    const cacheKey = `${scope}:${page}`
    if (isLoadingRef.current || !hasMore || fetchedPagesRef.current.has(cacheKey)) return
    fetchedPagesRef.current.add(cacheKey)
    isLoadingRef.current = true
    setIsLoading(true)
    const fetchPage = page
    ;(async () => {
      try {
        const res = await listSessions(fetchPage, PAGE_SIZE, scope)
        setItems((prev) => {
          if (fetchPage === 1) return res.items
          // Deduplicate by session_id when appending
          const existingIds = new Set(prev.map((it) => it.session_id))
          const newItems = res.items.filter((it) => !existingIds.has(it.session_id))
          return [...prev, ...newItems]
        })
        const total = (res as any)?.total
        if (typeof total === 'number') {
          const loaded = fetchPage * PAGE_SIZE
          setHasMore(loaded < total)
        } else {
          setHasMore((res.items?.length ?? 0) === PAGE_SIZE)
        }
      } catch (e) {
        try { console.error('Failed to load sessions', e) } catch { }
        setHasMore(false)
      } finally {
        isLoadingRef.current = false
        setIsLoading(false)
        setInitialLoadDone(true)
      }
    })()
  }, [page, hasMore, scope])

  // Infinite scroll using window scroll
  useEffect(() => {
    function onWindowScroll() {
      if (!hasMore || isLoadingRef.current) return
      const doc = document.documentElement
      const scrolled = window.innerHeight + window.scrollY
      const threshold = doc.scrollHeight - 200
      if (scrolled >= threshold) {
        setPage((p) => p + 1)
      }
    }
    window.addEventListener('scroll', onWindowScroll, { passive: true })
    return () => window.removeEventListener('scroll', onWindowScroll)
  }, [hasMore])

  // Auto-load until viewport can scroll or no more pages
  useEffect(() => {
    if (!hasMore || isLoadingRef.current) return
    const doc = document.documentElement
    if (doc.scrollHeight <= window.innerHeight + 50) {
      setPage((p) => p + 1)
    }
  }, [items.length, hasMore])

  const handleEnterSession = useCallback(async (sessionId: string) => {
    dispatch(openTab(sessionId))
    dispatch(setActiveTab(sessionId))
    dispatch(clearSessionFresh(sessionId))
    dispatch(setSessionLoading({ sessionId, isLoading: true }))
    dispatch(setSessionError({ sessionId, error: null }))
    const loadingStartTime = Date.now()
    const MIN_LOADING_TIME = 800

    try {
      localStorage.setItem('session_id', sessionId)
      dispatch(upsertSession({ id: sessionId, nodes: [], commits: [], messages: [], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), privacy: 'private', shared_with: [] } as any))
      try {
        window.history.pushState({}, '', `/session/${sessionId}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
      } catch { }
      ;(async () => {
        try {
          const info = await getSessionInfo(sessionId)
          dispatch(upsertSession(info))
          try {
            const commits = Array.isArray((info as any).commits) ? (info as any).commits : []
            const nodes = Array.isArray((info as any).nodes) ? (info as any).nodes : []
            let lastNodeId: string | null = null
            if (commits.length > 0) {
              const lastCommit = [...commits].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).at(-1)
              lastNodeId = lastCommit?.node_to_id ?? null
            }
            if (!lastNodeId && nodes.length > 0) {
              const lastNode = [...nodes].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).at(-1)
              lastNodeId = lastNode?.id ?? null
            }
            if (lastNodeId) dispatch(setPendingInitialSelection({ sessionId, nodeId: lastNodeId }))
          } catch { }

          const elapsedTime = Date.now() - loadingStartTime
          const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime)
          setTimeout(() => {
            dispatch(setSessionLoading({ sessionId, isLoading: false }))
          }, remainingTime)
        } catch (e: any) {
          dispatch(setSessionError({ sessionId, error: e?.message ?? 'Failed to load session details' }))
          const elapsedTime = Date.now() - loadingStartTime
          const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime)
          setTimeout(() => {
            dispatch(setSessionLoading({ sessionId, isLoading: false }))
          }, remainingTime)
        }
      })()
    } catch (e: any) {
      dispatch(setSessionError({ sessionId, error: e?.message ?? 'Failed to enter session' }))
      dispatch(setSessionLoading({ sessionId, isLoading: false }))
    }
  }, [dispatch])

  const getSessionTitle = (item: SessionListItem): string => {
    if (item.title && item.title.trim()) {
      return item.title
    }
    return t('untitledSession')
  }

  const isVideoUrl = (url: string | null | undefined): boolean => {
    if (!url) return false
    const cleanUrl = url.split('?')[0]?.split('#')[0]?.toLowerCase() ?? ''
    return (
      cleanUrl.endsWith('.mp4') ||
      cleanUrl.endsWith('.webm') ||
      cleanUrl.endsWith('.mov') ||
      cleanUrl.endsWith('.m4v')
    )
  }

  const showEmptyState = initialLoadDone && !isLoading && items.length === 0 && scope === 'my'
  const isSparse = initialLoadDone && !isLoading && items.length > 0 && items.length < 6 && scope === 'public'

  return (
    <div className={styles.root}>
      {whatsNew.isOpen && (
        <WhatsNew
          versions={whatsNew.versions}
          onClose={whatsNew.close}
          onDismiss={whatsNew.dismiss}
        />
      )}

      {loadingBarState !== 'hidden' && (
        <div className={styles.topLoadingBar}>
          <div className={`${styles.topLoadingBarFill} ${loadingBarState === 'completing' ? styles.topLoadingBarComplete : ''}`} />
        </div>
      )}

      <LandingHeader
        title="aether studio"
        actions={
          <button
            className={styles.newSessionBtn}
            onClick={handleCreate}
            aria-label={t('createNewSession')}
          >
            <PlusIcon className={styles.newSessionBtnIcon} />
            {t('newSession')}
          </button>
        }
      />

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${scope === 'public' || scope === 'all' ? styles.activeTab : ''}`}
          onClick={() => handleScopeChange(isDev() ? 'all' : 'public')}
        >
          <Globe className={styles.tabIcon} />
          {t('community')}
        </button>
        <button
          className={`${styles.tab} ${scope === 'my' ? styles.activeTab : ''}`}
          onClick={() => handleScopeChange('my')}
        >
          <Lock className={styles.tabIcon} />
          {t('mySessions')}
        </button>
      </div>

      <div className={`${styles.grid} ${isSparse ? styles.gridSparse : ''}`}>
        {/* Empty state for My Sessions */}
        {showEmptyState && (
          <div className={styles.emptyState}>
            <div
              className={styles.emptyCard}
              onClick={handleCreate}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleCreate()
                }
              }}
            >
              <PlusIcon className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>{t('startFirstSession')}</p>
              <p className={styles.emptySub}>{t('startFirstSessionSub')}</p>
            </div>
          </div>
        )}

        {/* Session cards */}
        {items.map((it) => {
          const thumb = it.saved_thumbnail_image || it.last_node_image
          const isVideo = isVideoUrl(thumb)

          return (
            <div
              key={it.session_id}
              className={styles.card}
              onClick={() => handleEnterSession(it.session_id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleEnterSession(it.session_id)
                }
              }}
            >
              <div className={styles.cardImageWrap}>
                {!thumb && (
                  <div className={styles.cardPlaceholder}>
                    <ImageIcon className={styles.placeholderIcon} strokeWidth={1} />
                  </div>
                )}
                {thumb && !isVideo && (
                  <img
                    src={thumb}
                    alt={getSessionTitle(it)}
                    loading="lazy"
                    onLoad={(e) => e.currentTarget.setAttribute('data-loaded', '')}
                  />
                )}
                {thumb && isVideo && (
                  <>
                    <video
                      src={thumb}
                      muted
                      playsInline
                      preload="metadata"
                      aria-label={getSessionTitle(it)}
                      onLoadedData={(e) => e.currentTarget.setAttribute('data-loaded', '')}
                      onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                      onMouseLeave={(e) => {
                        e.currentTarget.pause()
                        e.currentTarget.currentTime = 0
                      }}
                    />
                    <div className={styles.videoBadge}>
                      <Video className={styles.videoBadgeIcon} />
                    </div>
                  </>
                )}
              </div>

              <div className={styles.cardInfo}>
                <div className={styles.cardTitleRow}>
                  <span className={styles.sessionLabel}>{getSessionTitle(it)}</span>
                  {it.privacy === 'private' && (
                    <Lock className={styles.lockIcon} />
                  )}
                </div>
                <div className={styles.cardMeta}>
                  <span>{t('lastEdited', { time: formatRelativeTime(it.updated_at) })}</span>
                  {(it.node_count ?? 0) > 0 && (
                    <>
                      <span className={styles.metaDot} />
                      <span className={styles.nodeCountPill}>
                        <Layers className={styles.nodeCountIcon} />
                        {t('imageCount', { count: it.node_count ?? 0 })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {/* Loading Skeleton */}
        {isLoading && (items.length === 0 || hasMore) && [...Array(PAGE_SIZE)].map((_, i) => (
          <div key={`skeleton-${i}`} className={styles.skeletonCard}>
            <div className={styles.skeletonImage} />
            <div className={styles.skeletonInfo}>
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLineShort} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
